import React, { Fragment, useContext, useMemo } from "react"
import { useStaticQuery, graphql } from "gatsby"
import classNames from "../utils/classnames"
import Link from "./link"
import { campusUrlFromName } from "../utils/georgian"
import FlexTable from "./flex-table"
import { UserInterfaceContext } from "../context/UserInterfaceContext"

const PtsCourseListing = (): JSX.Element => {
  const { ptsCourseFilters } = useContext(UserInterfaceContext)
  const data: Queries.PtsCourseListingStaticQueryQuery = useStaticQuery(graphql`
    query PtsCourseListingStaticQuery {
      allPtsCourse(filter: { area: { ne: "MARI" } }) {
        totalCount
        nodes {
          Title
          Code
          Number
          Daytime
          Campuses
          Online
          Weekend
          Evening
          uri
          Categories
        }
      }
    }
  `)

  const courseCount = data.allPtsCourse.totalCount

  // filter keywords
  let courses = useMemo(
    () =>
      data.allPtsCourse.nodes.filter(course => {
        const haystack =
          classNames(
            course.Title || ``,
            course.Code || ``,
            course.Number || ``,
            course.Campuses?.join(` `) || ``,
            course.Daytime ? `daytime` : ``,
            course.Evening ? `evening` : ``,
            course.Weekend ? `weekend` : ``,
            course.Online ? `online` : ``
          ).toLowerCase() || ``
        const needle = ptsCourseFilters.keyword?.join(` `).toLowerCase() || ``
        // console.log({ haystack, needle })
        // const regResult = new RegExp(needle).test(haystack) // keyword 1 OR keyword 2
        return haystack.includes(needle) // keyword 1 AND keyword 2
      }),
    [ptsCourseFilters]
  )

  // filter campuses
  courses = useMemo(
    () =>
      ptsCourseFilters.campus && ptsCourseFilters.campus.length > 0
        ? courses.filter(course => {
            const haystack = course.Campuses?.join(` `).toLowerCase() || ``
            const needle =
              ptsCourseFilters.campus?.join(`|`).toLowerCase() || ``
            // console.log({ haystack, needle })
            return new RegExp(needle).test(haystack) // keyword 1 OR keyword 2
          })
        : courses,
    [ptsCourseFilters]
  )

  // filter times/study options
  courses = useMemo(
    () =>
      ptsCourseFilters.time && ptsCourseFilters.time.length > 0
        ? courses.filter(course => {
            const haystack =
              `${course.Daytime ? `daytime` : ``}${
                course.Evening ? `evening` : ``
              }${course.Weekend ? `weekend` : ``}${
                course.Online ? `online` : ``
              }` || ``
            const needle = ptsCourseFilters.time?.join(`|`).toLowerCase() || ``
            // console.log({ haystack, needle })
            return new RegExp(needle).test(haystack) // keyword 1 OR keyword 2
          })
        : courses,
    [ptsCourseFilters]
  )

  // filter times/study options
  courses = useMemo(
    () =>
      ptsCourseFilters.area && ptsCourseFilters.area.length > 0
        ? courses.filter(course => {
            const haystack = course.Categories?.join(` `).toLowerCase() || ``
            const needle = ptsCourseFilters.area?.join(`|`).toLowerCase() || ``
            // console.log({ haystack, needle })
            return new RegExp(needle).test(haystack) // keyword 1 OR keyword 2
          })
        : courses,
    [ptsCourseFilters]
  )

  const filteredCount = courses.length

  const rows = courses
    .filter(course => course.Code !== `MARI`)
    .map(course => [
      <Link key={1} className={classNames(`underline`)} to={course?.uri || ``}>
        {course.Title}
      </Link>,
      <Fragment key={4}>
        {course.Code} {course.Number}
      </Fragment>,
      <Fragment key={3}>
        {course?.Campuses?.map((campus, i) => {
          const link = campusUrlFromName(campus || ``)
          return (
            <Fragment key={i}>
              {link ? (
                <Link className={classNames(`underline`)} to={link}>
                  {campus}
                </Link>
              ) : (
                campus
              )}
              {(course.Campuses?.length || 0) > 1 &&
                i !== (course.Campuses?.length || 0) - 1 &&
                `, `}
            </Fragment>
          )
        })}
      </Fragment>,
      <Fragment key={4}>
        {course.Daytime && `Daytime`}
        {course.Evening ? (course.Daytime ? `, Evening` : `Evening`) : ``}
        {course.Weekend
          ? course.Daytime || course.Evening
            ? `, Weekend`
            : `Weekend`
          : ``}
        {course.Online
          ? course.Daytime || course.Evening || course.Weekend
            ? `, Online`
            : `Online`
          : ``}
      </Fragment>,
    ])

  return (
    <>
      <FlexTable
        footer={
          <Fragment>
            Showing {filteredCount} of {courseCount} courses
          </Fragment>
        }
        maxHeight={500}
        rows={rows}
        className="wp-block-georgiancollege-pts-course-listing"
        headings={[
          {
            label: `Title`,
            width: `w-4/12`,
          },
          {
            label: `Code`,
            width: `w-2/12`,
          },
          {
            label: `Campus`,
            width: `w-3/12`,
          },
          {
            label: `Time`,
            width: `w-3/12`,
          },
        ]}
      />
    </>
  )
}

export default PtsCourseListing
